import { useState, useCallback, useRef } from 'react';
import { X, Download, Star, Image as ImageIcon, ChevronDown, Sheet } from 'lucide-react';
import { useCanvasStore } from './canvas-store';
import { useActiveProjectImages, useActiveProjectCanvasAdCopy } from './store-selectors';
import type { AdCopyVariation } from './canvas-store';

interface CopySidebarPanelProps {
  open: boolean;
  onClose: () => void;
  projectId: number | null;
}

const STATUS_COLORS: Record<string, { bg: string; text: string }> = {
  draft: { bg: '#F3F4F6', text: '#6B7280' },
  starred: { bg: '#FEF9C3', text: '#713F12' },
  approved: { bg: '#DCFCE7', text: '#166534' },
};

function StatusBadge({ status }: { status: string }) {
  const colors = STATUS_COLORS[status] || STATUS_COLORS.draft;
  return (
    <span
      data-testid={`copy-status-badge-${status}`}
      style={{
        display: 'inline-block',
        padding: '2px 7px',
        borderRadius: 99,
        fontSize: 10,
        fontWeight: 600,
        background: colors.bg,
        color: colors.text,
        letterSpacing: '0.02em',
      }}
    >
      {status}
    </span>
  );
}

function AngleBadge({ angle }: { angle: string }) {
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '2px 7px',
        borderRadius: 99,
        fontSize: 10,
        fontWeight: 500,
        background: '#EEF2FF',
        color: '#4338CA',
      }}
    >
      {angle}
    </span>
  );
}

interface VariationRowProps {
  variation: AdCopyVariation;
  isActive: boolean;
  imageClientId: string;
  onSelect: () => void;
  onToggleStar: () => void;
  onStatusChange: (status: string) => void;
}

function VariationRow({ variation, isActive, imageClientId, onSelect, onToggleStar, onStatusChange }: VariationRowProps) {
  const isStarred = variation.status === 'starred';
  const isApproved = variation.status === 'approved';

  return (
    <div
      data-testid={`copy-row-${variation.id}`}
      onClick={onSelect}
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 10,
        padding: '10px 14px',
        cursor: 'pointer',
        background: isActive ? '#FAFAFA' : 'transparent',
        borderLeft: isActive ? '3px solid #6366f1' : '3px solid transparent',
        borderBottom: '1px solid #F3F3F1',
        transition: 'background 0.15s',
      }}
      onMouseEnter={(e) => {
        if (!isActive) (e.currentTarget as HTMLDivElement).style.background = '#FAFAFA';
      }}
      onMouseLeave={(e) => {
        if (!isActive) (e.currentTarget as HTMLDivElement).style.background = 'transparent';
      }}
    >
      <div style={{ fontSize: 11, color: '#8C8578', fontWeight: 600, minWidth: 28, paddingTop: 2 }}>
        #{variation.variationNumber}
      </div>

      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontSize: 13,
          fontWeight: 600,
          color: '#1A1A1A',
          marginBottom: 3,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}>
          {variation.headline || 'No headline'}
        </div>
        {variation.hook && (
          <div style={{
            fontSize: 11,
            color: '#5C5852',
            marginBottom: 4,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}>
            {variation.hook}
          </div>
        )}
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
          {variation.cta && (
            <span style={{
              fontSize: 10,
              padding: '2px 7px',
              borderRadius: 4,
              background: '#1A1A1A',
              color: '#FFFFFF',
              fontWeight: 600,
            }}>
              {variation.cta}
            </span>
          )}
          {variation.angle && <AngleBadge angle={variation.angle} />}
          <StatusBadge status={variation.status} />
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-end', flexShrink: 0 }}>
        <button
          data-testid={`copy-star-${variation.id}`}
          className="nodrag"
          onClick={(e) => { e.stopPropagation(); onToggleStar(); }}
          title={isStarred ? 'Unstar' : 'Star this variation'}
          style={{
            background: 'none',
            border: 'none',
            padding: 2,
            cursor: 'pointer',
            color: isStarred ? '#F59E0B' : '#C4C0BA',
          }}
        >
          <Star size={14} fill={isStarred ? '#F59E0B' : 'none'} />
        </button>
        {!isApproved && (
          <button
            data-testid={`copy-approve-${variation.id}`}
            onClick={(e) => { e.stopPropagation(); onStatusChange('approved'); }}
            style={{
              background: 'none',
              border: '1px solid #D1CDC6',
              borderRadius: 4,
              padding: '2px 6px',
              fontSize: 10,
              fontWeight: 600,
              color: '#5C5852',
              cursor: 'pointer',
            }}
          >
            Approve
          </button>
        )}
      </div>
    </div>
  );
}

export function CopySidebarPanel({ open, onClose, projectId }: CopySidebarPanelProps) {
  const {
    setCanvasAdCopyActiveIndex,
    updateCanvasAdCopyVariation,
  } = useCanvasStore();
  const canvasAdCopy = useActiveProjectCanvasAdCopy();
  const images = useActiveProjectImages();

  const [selectedImageFilter, setSelectedImageFilter] = useState<string | null>(null);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [sheetsExporting, setSheetsExporting] = useState(false);
  const exportMenuRef = useRef<HTMLDivElement>(null);

  const allImageIds = Object.keys(canvasAdCopy);
  const filteredImageIds = selectedImageFilter ? [selectedImageFilter] : allImageIds;

  const totalVariations = allImageIds.reduce((sum, id) => sum + (canvasAdCopy[id]?.variations?.length || 0), 0);

  const handleSelect = useCallback((imageClientId: string, index: number) => {
    setCanvasAdCopyActiveIndex(imageClientId, index);
  }, [setCanvasAdCopyActiveIndex]);

  const handleToggleStar = useCallback((imageClientId: string, variation: AdCopyVariation) => {
    const newStatus = variation.status === 'starred' ? 'draft' : 'starred';
    updateCanvasAdCopyVariation(imageClientId, variation.id, { status: newStatus });
    if (projectId) {
      fetch(`/api/canvas/projects/${projectId}/ad-copy/${variation.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus }),
      }).catch(() => {});
    }
  }, [updateCanvasAdCopyVariation, projectId]);

  const handleStatusChange = useCallback((imageClientId: string, variation: AdCopyVariation, status: string) => {
    updateCanvasAdCopyVariation(imageClientId, variation.id, { status });
    if (projectId) {
      fetch(`/api/canvas/projects/${projectId}/ad-copy/${variation.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status }),
      }).catch(() => {});
    }
  }, [updateCanvasAdCopyVariation, projectId]);

  const handleExportCSV = useCallback(() => {
    if (!projectId) return;
    setShowExportMenu(false);
    window.open(`/api/canvas/projects/${projectId}/ad-copy/export/csv`, '_blank');
  }, [projectId]);

  const handleExportSheets = useCallback(async () => {
    if (!projectId) return;
    setShowExportMenu(false);
    setSheetsExporting(true);
    try {
      const res = await fetch(`/api/canvas/projects/${projectId}/ad-copy/export/sheets`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      if (!res.ok) throw new Error('Export failed');
      const data = await res.json();

      if (data.success && data.sheetUrl) {
        window.open(data.sheetUrl, '_blank');
        return;
      }

      if (data.needsSetup) {
        const fallback = data.fallback;
        if (fallback?.rows) {
          const csvRows = [fallback.headers, ...fallback.rows];
          const csv = csvRows.map((row: string[]) =>
            row.map((cell: string) => `"${String(cell).replace(/"/g, '""')}"`).join(',')
          ).join('\n');
          const blob = new Blob([csv], { type: 'text/csv' });
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `${fallback.sheetTitle || 'ad-copy'}.csv`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(url);
        }
        return;
      }

      if (data.fallback?.rows) {
        const fallback = data.fallback;
        const csvRows = [fallback.headers, ...fallback.rows];
        const csv = csvRows.map((row: string[]) =>
          row.map((cell: string) => `"${String(cell).replace(/"/g, '""')}"`).join(',')
        ).join('\n');
        const blob = new Blob([csv], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${fallback.sheetTitle || 'ad-copy'}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }
    } catch (err) {
      console.error('[CopySidebar] Sheets export error:', err);
    } finally {
      setSheetsExporting(false);
    }
  }, [projectId]);

  const hasNoVariations = allImageIds.length === 0;

  return (
    <div
      data-testid="copy-sidebar-panel"
      style={{
        position: 'fixed',
        top: 0,
        right: 0,
        bottom: 0,
        width: 400,
        background: '#FFFFFF',
        boxShadow: '-4px 0 24px rgba(0,0,0,0.12)',
        zIndex: 250,
        display: 'flex',
        flexDirection: 'column',
        transform: open ? 'translateX(0)' : 'translateX(100%)',
        transition: 'transform 0.3s cubic-bezier(0.32, 0, 0.15, 1)',
      }}
    >
      {/* Header */}
      <div style={{
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '14px 16px',
        borderBottom: '1px solid #E5E2DC',
        background: '#FFFFFF',
      }}>
        <span data-testid="copy-sidebar-title" style={{ flex: 1, fontWeight: 700, fontSize: 14, color: '#111827' }}>
          Ad Copy Variations
          {totalVariations > 0 && (
            <span style={{ marginLeft: 8, fontSize: 12, fontWeight: 500, color: '#8C8578' }}>
              ({totalVariations})
            </span>
          )}
        </span>

        {/* Export dropdown */}
        <div ref={exportMenuRef} style={{ position: 'relative' }}>
          <button
            data-testid="copy-sidebar-export-btn"
            onClick={() => setShowExportMenu((v) => !v)}
            title="Export"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 4,
              padding: '5px 10px',
              border: '1px solid #E5E2DC',
              borderRadius: 6,
              background: '#FFFFFF',
              cursor: 'pointer',
              fontSize: 12,
              color: '#6B7280',
              fontWeight: 500,
            }}
          >
            <Download size={12} />
            Export
            <ChevronDown size={11} />
          </button>
          {showExportMenu && (
            <div
              data-testid="copy-export-menu"
              style={{
                position: 'absolute',
                top: 'calc(100% + 4px)',
                right: 0,
                background: '#FFFFFF',
                border: '1px solid #E5E2DC',
                borderRadius: 8,
                boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
                zIndex: 100,
                minWidth: 160,
                overflow: 'hidden',
              }}
            >
              <button
                data-testid="copy-sidebar-export-csv"
                onClick={handleExportCSV}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  width: '100%',
                  padding: '10px 14px',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: 13,
                  color: '#374151',
                  textAlign: 'left',
                }}
                onMouseEnter={(e) => (e.currentTarget.style.background = '#F9FAFB')}
                onMouseLeave={(e) => (e.currentTarget.style.background = 'none')}
              >
                <Download size={13} />
                Download CSV
              </button>
              <button
                data-testid="copy-sidebar-export-sheets"
                onClick={handleExportSheets}
                disabled={sheetsExporting}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  width: '100%',
                  padding: '10px 14px',
                  background: 'none',
                  border: 'none',
                  borderTop: '1px solid #F3F4F6',
                  cursor: sheetsExporting ? 'not-allowed' : 'pointer',
                  fontSize: 13,
                  color: sheetsExporting ? '#9CA3AF' : '#374151',
                  textAlign: 'left',
                }}
                onMouseEnter={(e) => { if (!sheetsExporting) (e.currentTarget.style.background = '#F9FAFB'); }}
                onMouseLeave={(e) => (e.currentTarget.style.background = 'none')}
              >
                <Sheet size={13} />
                {sheetsExporting ? 'Exporting…' : 'Export for Sheets'}
              </button>
            </div>
          )}
        </div>

        <button
          data-testid="copy-sidebar-close"
          onClick={onClose}
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            color: '#8C8578',
            display: 'flex',
            padding: 4,
          }}
        >
          <X size={16} />
        </button>
      </div>

      {/* Image filter pills */}
      {allImageIds.length > 1 && (
        <div style={{
          flexShrink: 0,
          padding: '10px 14px',
          borderBottom: '1px solid #E5E2DC',
          display: 'flex',
          gap: 6,
          flexWrap: 'wrap',
        }}>
          <button
            data-testid="copy-filter-all"
            onClick={() => setSelectedImageFilter(null)}
            style={{
              padding: '4px 10px',
              borderRadius: 99,
              border: `1.5px solid ${!selectedImageFilter ? '#6366f1' : '#E5E2DC'}`,
              background: !selectedImageFilter ? '#EEF2FF' : '#fff',
              color: !selectedImageFilter ? '#4338CA' : '#6B7280',
              fontSize: 11,
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            All images
          </button>
          {allImageIds.map((imageClientId) => {
            const image = images.find((img) => img.id === imageClientId);
            const count = canvasAdCopy[imageClientId]?.variations?.length || 0;
            const isSelected = selectedImageFilter === imageClientId;
            return (
              <button
                key={imageClientId}
                data-testid={`copy-filter-image-${imageClientId}`}
                onClick={() => setSelectedImageFilter(imageClientId)}
                style={{
                  padding: '4px 10px',
                  borderRadius: 99,
                  border: `1.5px solid ${isSelected ? '#6366f1' : '#E5E2DC'}`,
                  background: isSelected ? '#EEF2FF' : '#fff',
                  color: isSelected ? '#4338CA' : '#6B7280',
                  fontSize: 11,
                  fontWeight: 500,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4,
                }}
              >
                <ImageIcon size={10} />
                {image?.name?.split('.')[0] || imageClientId.slice(0, 8)}
                <span style={{ fontWeight: 700 }}>({count})</span>
              </button>
            );
          })}
        </div>
      )}

      {/* Variations list */}
      <div style={{ flex: 1, overflowY: 'auto' }} onClick={() => { if (showExportMenu) setShowExportMenu(false); }}>
        {hasNoVariations ? (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            padding: 32,
            textAlign: 'center',
            color: '#8C8578',
          }}>
            <div style={{ fontSize: 32, marginBottom: 16 }}>📝</div>
            <div style={{ fontSize: 14, fontWeight: 600, color: '#3D3A36', marginBottom: 8 }}>
              No variations yet
            </div>
            <div style={{ fontSize: 13, lineHeight: 1.6 }}>
              Select images on the canvas and click <strong>Ad Copy</strong> in the toolbar to generate copy variations.
            </div>
          </div>
        ) : (
          filteredImageIds.map((imageClientId) => {
            const adCopyState = canvasAdCopy[imageClientId];
            if (!adCopyState?.variations?.length) return null;
            const { variations, activeVariationIndex } = adCopyState;
            const image = images.find((img) => img.id === imageClientId);

            return (
              <div key={imageClientId}>
                {allImageIds.length > 1 && (
                  <div style={{
                    padding: '8px 14px',
                    fontSize: 11,
                    fontWeight: 700,
                    color: '#8C8578',
                    textTransform: 'uppercase',
                    letterSpacing: '0.06em',
                    background: '#F9F8F5',
                    borderBottom: '1px solid #F0EDE8',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                  }}>
                    <ImageIcon size={11} />
                    {image?.name || imageClientId.slice(0, 12)}
                    <span style={{ marginLeft: 'auto', fontWeight: 500 }}>({variations.length} variations)</span>
                  </div>
                )}
                {variations.map((variation, idx) => (
                  <VariationRow
                    key={variation.id}
                    variation={variation}
                    isActive={activeVariationIndex === idx}
                    imageClientId={imageClientId}
                    onSelect={() => handleSelect(imageClientId, idx)}
                    onToggleStar={() => handleToggleStar(imageClientId, variation)}
                    onStatusChange={(status) => handleStatusChange(imageClientId, variation, status)}
                  />
                ))}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
